import { FC, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { RootState } from 'src/redux/rootReducerType';
import { Certification } from 'src/types/Certification';
import { RENTER_PATHS, buildBookingPath } from 'src/routes/renterPaths';
import { zoneAbbreviation } from 'src/components/Compliance/Appointments/CalendarView';
import {
  fetchRenterBookingOptions,
  summarizeRenterUnits,
  useRenterAppointments,
} from './useRenterAppointments';

dayjs.extend(utc);
dayjs.extend(timezone);

/** Renter home card (ENG-3895): next upcoming appointment, or a
 * "Book a visit" CTA when the home allows online booking. Renders nothing
 * when neither applies, so homes without the feature see no change. */
const AppointmentsHomeCard: FC = () => {
  const navigate = useNavigate();
  const { upcoming, unavailable, loading } = useRenterAppointments();
  const certifications = useSelector(
    (state: RootState) => state.newRenterInterface.certifications
  ) as Certification[] | undefined;
  const [bookableCertId, setBookableCertId] = useState<number | null>(null);
  const [inviteExpiresAt, setInviteExpiresAt] = useState<string | null>(null);
  const [checked, setChecked] = useState(false);

  const activeCertIds = useMemo(
    () =>
      (certifications ?? [])
        .filter((c) => c.status?.renter_active !== false)
        .map((c) => c.id),
    [certifications]
  );

  useEffect(() => {
    let cancelled = false;
    if (unavailable || activeCertIds.length === 0) {
      setChecked(true);
      return undefined;
    }
    // Find the first active cert whose property is bookable right now.
    (async () => {
      for (const certId of activeCertIds) {
        try {
          const options = await fetchRenterBookingOptions(certId);
          if (options?.bookable) {
            if (!cancelled) {
              setBookableCertId(certId);
              setInviteExpiresAt(options.invite?.expires_at ?? null);
            }
            break;
          }
        } catch {
          // ignore — treat as not bookable
        }
      }
      if (!cancelled) {
        setChecked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeCertIds, unavailable]);

  const next = upcoming[0];
  const nextUnits = summarizeRenterUnits(next?.units);
  if (loading && !checked) {
    return null;
  }
  if (!next && !bookableCertId) {
    return null;
  }

  const listPath = `/${RENTER_PATHS.HOME}/${RENTER_PATHS.APPOINTMENTS}`;

  return (
    <Card sx={{ borderRadius: 2 }}>
      <CardContent>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '12px',
              bgcolor: 'primary.lighter',
              color: 'primary.dark',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <EventAvailableIcon fontSize="small" />
          </Box>
          <Stack spacing={0.5} flexGrow={1}>
            {next ? (
              <>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle1">
                    Your next appointment
                  </Typography>
                  <Chip size="small" color="info" label={next.category_name} />
                </Stack>
                <Typography variant="body1" fontWeight={600}>
                  {dayjs(next.start_at)
                    .tz(next.timezone)
                    .format('ddd, MM/DD/YYYY [at] h:mm A')}{' '}
                  {zoneAbbreviation(next.timezone)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {next.location || next.property_name}
                  {next.host ? ` · with ${next.host.name}` : ''}
                </Typography>
                {nextUnits.labels && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      Units: {nextUnits.labels}
                    </Typography>
                    {nextUnits.extra > 0 && (
                      <Chip size="small" label={`+${nextUnits.extra}`} />
                    )}
                  </Stack>
                )}
                <Stack direction="row" spacing={1} pt={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => navigate(listPath)}
                  >
                    View Details
                  </Button>
                  {next.can_reschedule && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={() =>
                        navigate(
                          buildBookingPath(
                            next.certification,
                            next.id,
                            next.category
                          )
                        )
                      }
                    >
                      Move
                    </Button>
                  )}
                </Stack>
              </>
            ) : (
              <>
                <Typography variant="subtitle1">
                  You&apos;re invited to book a visit
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pick a time that works for you. We&apos;ll send a reminder.
                  {inviteExpiresAt
                    ? ` Please book by ${dayjs(inviteExpiresAt).format(
                        'MM/DD/YYYY'
                      )}.`
                    : ''}
                </Typography>
                <Stack direction="row" spacing={1} pt={1}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() =>
                      navigate(buildBookingPath(bookableCertId as number))
                    }
                  >
                    Book a Visit
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => navigate(listPath)}
                  >
                    My Appointments
                  </Button>
                </Stack>
              </>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default AppointmentsHomeCard;
